import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HasItems } from '../types';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { createHttpParamsFromRecord } from '../secure/common/helpers';
import {
  AssociateLocationObjectsRequest,
  AType,
  GetLocationObjectOptions,
  LocationObjectAssociation,
  LocationObjectAttributesByType,
  LocationObjectWithAssociations,
  OType,
} from '../types/location-object';

@Injectable({
  providedIn: 'root',
})
export class LocationObjectService {
  private httpClient = inject(HttpClient);
  readonly apiHost = `${environment.apiBaseUrl}`;

  getLocationObjectsOfType<T extends OType>(
    locationId: number,
    otype: T,
    options?: GetLocationObjectOptions,
  ): Observable<HasItems<LocationObjectWithAssociations<T>>> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Objects/${otype}`;
    const params = createHttpParamsFromRecord({
      include_associations: options?.include_associations ? 'true' : 'false',
      atype: options?.atype,
    });
    return this.httpClient.get<HasItems<LocationObjectWithAssociations<T>>>(requestUrl, {
      params,
    });
  }

  getLocationObjectOfTypeById<T extends OType>(
    locationId: number,
    otype: T,
    objectId: string,
    options?: GetLocationObjectOptions,
  ): Observable<LocationObjectWithAssociations<T>> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Objects/${otype}/${objectId}`;
    const params = createHttpParamsFromRecord({
      include_associations: options?.include_associations ? 'true' : 'false',
      atype: options?.atype,
    });
    return this.httpClient.get<LocationObjectWithAssociations<T>>(requestUrl, {
      params,
    });
  }

  createLocationObject<T extends OType>(
    locationId: number,
    otype: T,
    attributes: LocationObjectAttributesByType[T],
  ): Observable<LocationObjectWithAssociations<T>> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Objects/${otype}`;
    return this.httpClient.post<LocationObjectWithAssociations<T>>(requestUrl, {
      attributes,
    });
  }

  updateLocationObject<T extends OType>(
    locationId: number,
    otype: OType,
    objectId: string,
    attributes: LocationObjectAttributesByType[T],
  ): Observable<LocationObjectWithAssociations<T>> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Objects/${otype}/${objectId}`;
    return this.httpClient.put<LocationObjectWithAssociations<T>>(requestUrl, {
      attributes,
    });
  }

  deleteLocationObject(locationId: number, otype: OType, objectId: string): Observable<void> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Objects/${otype}/${objectId}`;
    return this.httpClient.delete<void>(requestUrl);
  }

  associateLocationObjects<T extends AType>(request: {
    locationId: number;
    otype: OType;
    objectId1: string;
    payload: AssociateLocationObjectsRequest<T>;
  }): Observable<LocationObjectAssociation<T>> {
    const { locationId, otype, objectId1, payload } = request;
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Objects/${otype}/${objectId1}/Associations/${payload.type}`;
    return this.httpClient.post<LocationObjectAssociation<T>>(requestUrl, payload);
  }

  deleteLocationObjectsAssociation(request: {
    locationId: number;
    otype: OType;
    objectId1: string;
    atype: AType;
    objectId2: string;
  }): Observable<void> {
    const { locationId, otype, objectId1, atype, objectId2 } = request;
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Objects/${otype}/${objectId1}/Associations/${atype}/${objectId2}`;
    return this.httpClient.delete<void>(requestUrl);
  }
}
